import { api, endpoints } from './api';

export interface DeviceLifecycleSummary {
  state: string;
  reason: string;
  provisioning_status?: string | null;
  deployment_status?: string | null;
  deployment_config_type?: string | null;
  deployment_id?: string | null;
  post_deploy_status?: string | null;
  edge_liveness_status?: string | null;
  decommission_phase?: string | null;
}

export interface Device {
  id: string;
  device_id: string;
  name: string;
  description: string;
  device_type: string;
  location: string;
  group_id?: string;
  status: string;
  last_seen: string;
  current_config_version: string;
  capabilities: any;
  device_metadata: any;
  ip_address?: string;
  mac_address?: string;  // Hardware identifier (wlan0 MAC)
  provisioning_status?: string;
  has_pending_token?: boolean;
  token_expires_at?: string | null;
  activated_at?: string | null;
  heartbeat_age_s?: number | null;
  edge_alive?: boolean;
  edge_liveness_status?: string;
  edge_offline_threshold_sec?: number;
  edge_workload_status?: 'ok' | 'degraded' | 'down' | 'unknown' | 'not_configured' | string;
  configured_camera_count?: number;
  pipeline_state?: string | null;
  pipeline_state_age_s?: number | null;
  lifecycle_summary?: DeviceLifecycleSummary;
}

export interface DeviceGroup {
  id: string;
  name: string;
  description: string;
  location: string;
  tags: any;
  device_count: number;
}

export interface CreateDeviceData {
  device_id: string;
  name: string;
  description?: string;
  device_type: string;
  location?: string;
  group_id?: string;
  capabilities?: any;
  device_metadata?: any;
}

export interface UpdateDeviceData {
  name?: string;
  description?: string;
  device_type?: string;
  location?: string;
  group_id?: string;
  capabilities?: any;
  device_metadata?: any;
}

export const devicesApi = {
  // List all devices
  async listDevices(): Promise<Device[]> {
    const response = await api.get(endpoints.devices.list);
    // API responses in this repo have historically used a few shapes:
    // - { items: Device[] }
    // - { devices: Device[], pagination: ... }
    // - Device[] (legacy)
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray((data as any).items)) return (data as any).items;
    if (data && Array.isArray((data as any).devices)) return (data as any).devices;
    return [];
  },

  /** List device IDs for site-scoped UI (Zone Editor). Merges devices + incident_device_ids. */
  async listDeviceIdsForSite(): Promise<string[]> {
    const response = await api.get(endpoints.devices.list);
    const data = response.data;
    const devices = Array.isArray(data) ? data : (data?.devices || data?.items || []);
    const fromDevices = (devices as { device_id?: string }[]).map((d) => d.device_id).filter((x): x is string => Boolean(x));
    const fromIncidents = (data?.incident_device_ids || []) as string[];
    return Array.from(new Set([...fromDevices, ...fromIncidents])).filter(Boolean).sort();
  },

  // Get a single device
  async getDevice(id: string): Promise<Device> {
    const response = await api.get(endpoints.devices.get(id));
    return response.data.device || response.data;  // API wraps in {device: ...}
  },

  // Create a new device
  async createDevice(data: CreateDeviceData): Promise<Device> {
    const response = await api.post(endpoints.devices.create, data);
    return response.data;
  },

  // Update a device
  async updateDevice(id: string, data: UpdateDeviceData): Promise<Device> {
    const response = await api.put(endpoints.devices.update(id), data);
    return response.data;
  },

  // Delete a device
  async deleteDevice(id: string): Promise<void> {
    await api.delete(endpoints.devices.delete(id));
  },

  // List all device groups
  async listGroups(): Promise<DeviceGroup[]> {
    const response = await api.get(endpoints.devices.groups);
    return response.data.items || response.data;
  },

  // Get device by device_id (not UUID)
  async getDeviceByDeviceId(deviceId: string): Promise<Device | null> {
    try {
      const devices = await this.listDevices();
      return devices.find(d => d.device_id === deviceId) || null;
    } catch {
      return null;
    }
  },

  // Generate activation token for a device
  async generateToken(deviceId: string): Promise<{ token: string; expires_at: string }> {
    const response = await api.post(`/api/v1/provisioning/devices/${deviceId}/token`);
    return response.data;
  },

  // Pipeline control commands
  pipeline: {
    async start(deviceId: string): Promise<any> {
      const response = await api.post(`/api/v1/pipeline/devices/${deviceId}/pipeline/start`);
      return response.data;
    },

    async stop(deviceId: string): Promise<any> {
      const response = await api.post(`/api/v1/pipeline/devices/${deviceId}/pipeline/stop`);
      return response.data;
    },

    async restart(deviceId: string): Promise<any> {
      const response = await api.post(`/api/v1/pipeline/devices/${deviceId}/pipeline/restart`);
      return response.data;
    },

    async switch(deviceId: string, pipelineName: string): Promise<any> {
      const response = await api.post(`/api/v1/pipeline/devices/${deviceId}/pipeline/switch`, {
        pipeline: pipelineName,
      });
      return response.data;
    },

    async getStatus(deviceId: string): Promise<any> {
      const response = await api.get(`/api/v1/pipeline/devices/${deviceId}/pipeline/status`);
      return response.data;
    },

    async listPipelines(deviceId: string): Promise<any> {
      const response = await api.get(`/api/v1/pipeline/devices/${deviceId}/pipeline/list`);
      return response.data;
    },
  },
};

export default devicesApi;

